import { ConfigProvider } from 'antd'
import { Suspense, lazy } from 'react'
import { Provider as StoreProvider } from 'react-redux'
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { IntercomProvider } from 'react-use-intercom'

import { store } from '@Store/index'
import { DebugModeProvider } from '@Utils/context/modal/useDebugMode'
import { SPRuleTemplateWarningProvider } from '@Utils/context/modal/useSPRuleTemplateWarning'
import OnboardingGuard from '@Utils/guards/OnboardingGuard'
import SpinLoader from '@Utils/loaders/spin'

import AppLayout from './layouts/AppLayout'
import CompanyStatusCheck from './layouts/company-status-check'
import PackageCheck from './layouts/package-check'
import { PlatinumFeatures, PremiumFeatures } from './store/types/billing'
import { NavigateBillingModalProvider } from './utils/context/modal/use-navigate-billing-modal'
import { SPRuleFrequencyWarningProvider } from './utils/context/modal/useSPRuleFrequencyWarning'
import ErrorHandler from './utils/error/ErrorHandler'
import DashboardIndex from './views/dashboard'
import Product from './views/dashboard/product'

// const Product = lazy(async () => await import('./views/dashboard/product'))

const EmptyLayout = lazy(async () => await import('@Layouts/EmptyLayout'))

const RegisterCallback = lazy(
  async () => await import('./views/auth/register-callback'),
)

const OnboardingHome = lazy(async () => await import('@Pages/Onboarding'))
const Billing = lazy(
  async () => await import('./views/dashboard/billing-pricing/billing'),
)
const CustomPlanPayment = lazy(
  async () => await import('./views/dashboard/billing-pricing/custom-plan'),
)
const DisabledCompany = lazy(
  async () =>
    await import('./views/dashboard/billing-pricing/disabled-company'),
)
const OneTimePayment = lazy(
  async () =>
    await import('./views/dashboard/billing-pricing/one-time-payment'),
)
const Pricing = lazy(
  async () => await import('./views/dashboard/billing-pricing/pricing'),
)
const DynamicPricing = lazy(
  async () => await import('./views/dashboard/dynamic-pricing'),
)
const AddNewRule = lazy(
  async () => await import('./views/dashboard/dynamic-pricing/add-new-rule'),
)
const EditRule = lazy(
  async () => await import('./views/dashboard/dynamic-pricing/edit-rule'),
)
const DynamicPricingHistory = lazy(
  async () => await import('./views/dashboard/dynamic-pricing/history'),
)
const HistoryPage = lazy(
  async () =>
    await import('./views/dashboard/product/history-page/history-page'),
)
const BrandDetails = lazy(
  async () => await import('./views/dashboard/reports/brand-details'),
)
const BrandReports = lazy(
  async () => await import('./views/dashboard/reports/brand-reports'),
)
const CategoryDetails = lazy(
  async () => await import('./views/dashboard/reports/category-details'),
)
const CategoryReports = lazy(
  async () => await import('./views/dashboard/reports/category-reports'),
)
const ExportReports = lazy(
  async () => await import('./views/dashboard/reports/export-reports'),
)
const ClosedReportsView = lazy(
  async () => await import('./views/dashboard/url-dashboard/closed-reports'),
)
const OpenReportsView = lazy(
  async () => await import('./views/dashboard/url-dashboard/open-reports'),
)
const Reports404View = lazy(
  async () => await import('./views/dashboard/url-dashboard/reports-404'),
)
const Manage = lazy(async () => await import('./components/product/manage'))
const PrisyncSettings = lazy(
  async () => await import('./views/dashboard/prisync-settings'),
)
const ShopifyImport = lazy(
  async () => await import('./components/shopify/ShopifyImportContext'),
)
const LoginAs = lazy(async () => await import('./views/auth/login-as'))
const LoginWithShopify = lazy(
  async () => await import('./views/auth/login-with-shopify'),
)
const GoogleCallback = lazy(
  async () => await import('./views/auth/social-login-callback/google'),
)
const LinkedInCallback = lazy(
  async () => await import('./views/auth/social-login-callback/linkedin'),
)
const ForgotPassword = lazy(
  async () => await import('./views/auth/forgot-password'),
)
const PasswordResetConfirm = lazy(
  async () => await import('./views/auth/password-reset-confirm'),
)
const SetNewPassword = lazy(
  async () => await import('./views/auth/set-new-password'),
)
const SignIn = lazy(async () => await import('./views/auth/sign-in'))
const SignUp = lazy(async () => await import('./views/auth/sign-up'))

const AuthCallback = lazy(
  async () => await import('./views/auth/auth-callback'),
)
const ShopifyInstall = lazy(
  async () => await import('./views/auth/shopify-install'),
)
const ShopifyCompleteInstallation = lazy(
  async () => await import('./views/auth/shopify-register'),
)
const PaymentError = lazy(
  async () => await import('./utils/error/PaymentError'),
)
const ProductSource = lazy(async () => await import('@Pages/ProductSource'))
const CompetitorSource = lazy(
  async () => await import('@Pages/CompetitorSource'),
)
const DataManagement = lazy(async () => await import('@Pages/DataManagement'))

const routes = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        element={
          <SPRuleFrequencyWarningProvider>
            <SPRuleTemplateWarningProvider>
              <AppLayout />
            </SPRuleTemplateWarningProvider>
          </SPRuleFrequencyWarningProvider>
        }
        ErrorBoundary={ErrorHandler}
      >
        <Route path="/" element={<CompanyStatusCheck />}>
          <Route element={<OnboardingGuard />}>
            <Route index element={<DashboardIndex />} />
            <Route path="dashboard" element={<DashboardIndex />} />
            <Route path="products/">
              <Route
                path="history/:id"
                element={<PackageCheck allowed={PlatinumFeatures} />}
              >
                <Route index element={<HistoryPage />} />
              </Route>
              <Route index element={<Product />} />
            </Route>
            <Route path="manage/:type?" element={<Manage />} />
            <Route path="data-management">
              <Route path="products">
                <Route path="shopify-import" element={<ShopifyImport />} />
                <Route index element={<ProductSource />} />
              </Route>
              <Route path="competitors" element={<CompetitorSource />} />
              <Route index element={<DataManagement />} />
            </Route>
            <Route
              path="dynamic-pricing/"
              element={<PackageCheck allowed={PremiumFeatures} />}
            >
              <Route index element={<DynamicPricing />} />
              <Route path="add-new-rule/" element={<AddNewRule />} />
              <Route path="edit-rule/:id" element={<EditRule />} />
              <Route path="template/:id" element={<EditRule isTemplate />} />
            </Route>
            <Route
              path="dynamic-pricing/history"
              element={<DynamicPricingHistory />}
            />
            <Route path="reports/export" element={<ExportReports />} />
            <Route
              path="reports/"
              element={<PackageCheck allowed={PremiumFeatures} />}
            >
              <Route path="brand" element={<BrandReports />} />
              <Route path="brand/details/:id" element={<BrandDetails />} />
              <Route path="category" element={<CategoryReports />} />
              <Route
                path="category/details/:id"
                element={<CategoryDetails />}
              />
            </Route>

            {/*<Route path="reports/" element={<ReportsView />}></Route>*/}
            <Route path="settings/:type?" element={<PrisyncSettings />} />
            <Route path="billing/" element={<Billing />} />
            <Route path="billing/plans" element={<Pricing />} />
            <Route
              path="payment/custom-plan-payment"
              element={<CustomPlanPayment />}
            />
            <Route
              path="payment/one-time-payment"
              element={<OneTimePayment />}
            />
            <Route path="/payment-error/:from?" element={<PaymentError />} />
            <Route path="disabled-company" element={<DisabledCompany />} />
            <Route path="closed-reports/" element={<ClosedReportsView />} />
            <Route path="open-reports/" element={<OpenReportsView />} />
            <Route path="404-reports/" element={<Reports404View />} />
            <Route path="onboarding/" element={<OnboardingHome />} />
          </Route>
        </Route>
      </Route>

      <Route path="/shopify">
        <Route
          path="complete-installation"
          element={<ShopifyCompleteInstallation />}
        />
        <Route path="app" element={<ShopifyInstall />} />
      </Route>
      {(CURRENT_ENV === 'dev' || CURRENT_ENV === 'test') && (
        <>
          <Route path="/login" element={<SignIn />} />
          <Route path="/register" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<SetNewPassword />} />
          <Route
            path="/password-reset-confirm"
            element={<PasswordResetConfirm />}
          />
        </>
      )}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route element={<EmptyLayout />}>
        <Route path="/auth/login-as" element={<LoginAs />} />
        <Route path="/auth/login-with-shopify" element={<LoginWithShopify />} />
        <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/auth/register-callback" element={<RegisterCallback />} />
      </Route>
    </>,
  ),
)

// routes.subscribe(({ location }) => {
//   console.log('router.subscribe', location.pathname, location.search)
// })

function App(): JSX.Element {
  return (
    <DebugModeProvider>
      <StoreProvider store={store}>
        <IntercomProvider
          autoBoot={true}
          initializeDelay={3000}
          appId={INTERCOM_ID as string}
        >
          <ConfigProvider>
            <NavigateBillingModalProvider>
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
              <Suspense
                fallback={
                  <div className="pt-9">
                    <SpinLoader />
                  </div>
                }
              >
                <RouterProvider router={routes} />
              </Suspense>
              <ToastContainer />
            </NavigateBillingModalProvider>
          </ConfigProvider>
        </IntercomProvider>
      </StoreProvider>
    </DebugModeProvider>
  )
}

export default App
